/**
 * AICA Button Component - Meridian Dark Design System
 *
 * BUTTON HIERARCHY (use in this order of emphasis):
 *
 * 1. PRIMARY (default) - Main CTA, single per screen section
 *    - Teal background, white text, glow on hover
 *    - Use for: Save, Submit, Create, Confirm
 *
 * 2. SECONDARY - Alternative primary action with teal border
 *    - Transparent with teal border, teal text
 *    - Use for: Cancel that should still be visible, alternative CTAs
 *
 * 3. OUTLINE - Secondary actions, neutral emphasis
 *    - Gray border, gray text, subtle hover
 *    - Use for: Edit, View Details, secondary actions
 *
 * 4. GHOST - Tertiary actions, minimal emphasis but still visible
 *    - No border, subtle background on hover
 *    - Use for: Close, Back, icon-only buttons, table row actions
 *
 * 5. LINK - Text-only, inline with content
 *    - No background, underline on hover
 *    - Use for: Inline links, "Learn more", navigation
 *
 * 6. DANGER - Destructive actions (red)
 *    - Red background, white text
 *    - Use for: Delete, Remove, Revoke
 *
 * ICON RULES:
 * - Icon-only buttons: Use ghost or outline variant with size="icon-sm|icon-md|icon-lg"
 *   (a square of the same height as the text sizes)
 * - Icon + text: Icon on left (16px), 8px gap
 * - Loading state: Replace icon with spinner
 */

import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CONTROL_SQUARE } from './control-size';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual variant of the button:
   * - primary: Main CTA (teal bg)
   * - secondary: Alt CTA (teal border)
   * - outline: Secondary action (gray border)
   * - ghost: Tertiary action (minimal)
   * - link: Text-only link style
   * - danger: Destructive action (red)
   *
   * Aliases (backward compat): default=primary, destructive=danger, quiet=ghost
   */
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'ghost'
    | 'link'
    | 'danger'
    // Backward compatibility
    | 'default'
    | 'destructive'
    | 'quiet';
  /**
   * Control size. `sm` 32px, `md` 36px, `lg` 40px tall. The `icon-*` sizes are
   * the square icon-only button of the same height (32/36/40 px, no padding):
   * an icon button in a row with an Input or Select of the same size name takes
   * its height from here, never from a `h-8 w-8` at the call site.
   */
  size?: 'sm' | 'md' | 'lg' | 'icon-sm' | 'icon-md' | 'icon-lg';
  /** Shows a spinner and disables the button during async operations */
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const variants: Record<string, string> = {
      // 1. PRIMARY - Main CTA (teal background)
      primary:
        'bg-primary hover:bg-primary-hover active:bg-primary-active text-bg-void font-medium hover:shadow-glow-primary',

      // 2. SECONDARY - Alternative CTA (teal border)
      secondary:
        'bg-transparent hover:bg-primary-dim text-primary-fg border border-primary font-medium hover:shadow-glow-subtle',

      // 3. OUTLINE - Secondary action (gray border, clearly visible)
      outline:
        'bg-transparent hover:bg-bg-surface text-fg border border-border hover:border-border-emphasis hover:shadow-glow-subtle',

      // 4. GHOST - Tertiary action (minimal but visible on hover)
      ghost:
        'bg-transparent hover:bg-bg-elevated text-fg-muted border border-transparent hover:border-bg-muted hover:text-fg',

      // 5. LINK - Text-only, inline
      link: 'bg-transparent hover:underline text-primary hover:text-primary-hover p-0 min-h-0',

      // 6. DANGER - Destructive (red background)
      danger:
        'bg-danger hover:bg-danger-hover active:bg-danger-active text-bg-void font-medium hover:shadow-glow-error',

      // Backward compatibility aliases
      default:
        'bg-primary hover:bg-primary-hover active:bg-primary-active text-bg-void font-medium hover:shadow-glow-primary',
      destructive:
        'bg-danger hover:bg-danger-hover active:bg-danger-active text-bg-void font-medium hover:shadow-glow-error',
      quiet:
        'bg-transparent hover:bg-bg-elevated text-fg-muted border border-transparent hover:border-bg-muted hover:text-fg',
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-sm rounded-button min-h-[32px]',
      md: 'px-4 py-2 text-sm rounded-button min-h-[36px]',
      lg: 'px-5 py-2.5 text-sm rounded-button min-h-[40px]',
      // Square icon-only buttons: width and height from the one control scale.
      'icon-sm': cn(CONTROL_SQUARE.sm, 'p-0 text-sm rounded-button'),
      'icon-md': cn(CONTROL_SQUARE.md, 'p-0 text-sm rounded-button'),
      'icon-lg': cn(CONTROL_SQUARE.lg, 'p-0 text-sm rounded-button'),
    };

    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center cursor-pointer whitespace-nowrap',
          // Meridian Dark: Executive animations with subtle glow
          'transition-all duration-150 ease-standard',
          'hover:-translate-y-px active:translate-y-0',
          // Color and shadow transitions
          'transition-[colors,box-shadow,transform] duration-150',
          // Focus states with teal ring
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          // Disabled states
          'disabled:opacity-50 disabled:pointer-events-none disabled:hover:translate-y-0 disabled:cursor-not-allowed',
          // Reduced motion support
          'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
          variants[variant],
          sizes[size],
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2 shrink-0" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
